import React, { Key, useEffect, useMemo } from "react"
import { Alert, Button, Flex, Spin, message } from "antd"
import { SponsoredBrandsTable } from "./SponsoredBrandsTable"
import { useTypedSelector } from "../../../hooks/useTypedSelector"
import { useActions } from "../../../hooks/useActions"
import { ICampaignResult, SB_CAMPAIGNS_STATE_FILTER } from "../types/sbCampaignApi.types"
import { setStateType } from "../../../types/setStateType"
import { campaignStates } from "../../../types/campaigns"
import ProjectGroupList from "../../../components/ProjectGroups/ProjectGroupList"
import { buildProjectGroups } from "../../../components/ProjectGroups/projectGroups.helper"
import { buildSbCampaignsBody } from "../services/sbCampaignsBody.service"

interface ISponsoredBrandsProjectGroups {
  isManaged: boolean
  stateFilter: SB_CAMPAIGNS_STATE_FILTER
  isExpandedAll: boolean
  expandedRowCampaignIds: readonly Key[]
  setExpandedRowCampaignIds: setStateType<readonly Key[]>
  selectedTargetIds: Key[]
  setSelectedTargetIds: setStateType<Key[]>
  campaignDataForTable: ICampaignResult[] | null
  setCampaignDataForTable: setStateType<ICampaignResult[] | null>
  setSelectedRowCampaignIds: setStateType<Key[]>
  selectedRowCampaignIds: Key[]
}

const SponsoredBrandsProjectGroups: React.FC<ISponsoredBrandsProjectGroups> = (props) => {
  const {
    isManaged,
    stateFilter,
    isExpandedAll,
    campaignDataForTable,
    setExpandedRowCampaignIds,
    setSelectedRowCampaignIds,
  } = props
  const { dateRange, isOrder } = useTypedSelector((select) => select.filter)
  const { loading, error } = useTypedSelector((select) => select.sponsoredBrands)
  const { fetchSponsoredBrandsCampaigns } = useActions()

  function loadCampaigns() {
    fetchSponsoredBrandsCampaigns(
      buildSbCampaignsBody({ dateRange, isOrder, isManaged, stateFilter })
    )
  }

  useEffect(() => {
    loadCampaigns()
    setExpandedRowCampaignIds([])
    setSelectedRowCampaignIds([])
  }, [dateRange, isOrder, isManaged, stateFilter])

  useEffect(() => {
    if (error) {
      message.error("Failed to load campaigns")
    }
  }, [error])

  const groups = useMemo(
    () =>
      buildProjectGroups(campaignDataForTable || [], {
        getProject: (campaign) => campaign.project,
        getSpend: (campaign) => campaign.spend,
        getSales: (campaign) => campaign.sales,
        isActive: (campaign) => campaign.campaignStatus === campaignStates.ENABLED,
      }),
    [campaignDataForTable]
  )

  if (loading && groups.length === 0) {
    return (
      <Flex justify="center" style={{ padding: 40 }}>
        <Spin />
      </Flex>
    )
  }

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="Failed to load campaigns"
        action={
          <Button size="small" danger onClick={loadCampaigns}>
            Retry
          </Button>
        }
      />
    )
  }

  return (
    <ProjectGroupList
      groups={groups}
      isExpandedAll={isExpandedAll}
      renderGroupContent={(group) => (
        <SponsoredBrandsTable
          campaigns={group.campaigns}
          expandedRowCampaignIds={props.expandedRowCampaignIds}
          setExpandedRowCampaignIds={props.setExpandedRowCampaignIds}
          selectedTargetIds={props.selectedTargetIds}
          setSelectedTargetIds={props.setSelectedTargetIds}
          setCampaignDataForTable={props.setCampaignDataForTable}
          setSelectedRowCampaignIds={props.setSelectedRowCampaignIds}
          selectedRowCampaignIds={props.selectedRowCampaignIds}
        />
      )}
    />
  )
}

export default SponsoredBrandsProjectGroups
